
import React from 'react';
import { useForm } from 'react-hook-form';
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useCategoryStore } from "@/store/api/categoryStore";
import { useUserStore } from "@/store/userStore";
import { useFormDraft } from "@/hooks/useFormDraft";
import { buildDraftKey, useFormDraftStore } from "@/store/formDraftStore";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useTranslation } from "react-i18next";
import { resolveCalendarColor, readableTextColor } from "@/lib/calendarColors";
import { Check } from "lucide-react";

interface AddCourseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (courseData: any) => Promise<boolean>;
  editingCourse?: any;
}

export const AddCourseDialog = ({
  open,
  onOpenChange,
  onSave,
  editingCourse,
}: AddCourseDialogProps) => {
  const { t } = useTranslation(['courses']);
  const { categories, fetchCategories } = useCategoryStore();
  const [calendars, setCalendars] = useState<Array<{ id: string; name: string; color?: string | null }>>([]);
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<string[]>([]);
  // Vrai dès que l'utilisateur touche à la sélection de plannings. Sert de
  // garde-fou : si le cours édité vient du repli en lecture directe (edge
  // injoignable), `editingCourse.calendar_ids` est absent — envoyer `[]` sans
  // action de l'utilisateur effacerait ses affectations réelles.
  const [calendarsTouched, setCalendarsTouched] = useState(false);
  
  const courseSchema = z.object({
    name: z.string().min(1, t('courseNameRequired', 'Le nom du cours est obligatoire')),
    description: z.string().optional().default(""),
    category_id: z.string().min(1, t('categoryRequired', 'La catégorie est obligatoire')),
    default_capacity: z.number().min(1),
    default_duration: z.number().min(15),
  });

  const form = useForm<z.infer<typeof courseSchema>>({
    resolver: zodResolver(courseSchema),
    defaultValues: {
      name: "",
      description: "",
      category_id: "",
      default_capacity: 10,
      default_duration: 60,
    },
  });

  // Brouillon persisté (course-form:{id}/:new) superposé dans l'effet reset
  // ci-dessous. La sélection de plannings vit HORS react-hook-form : elle est
  // brouillonnée sous une clé sœur, sauvegardée seulement quand l'utilisateur
  // a réellement touché la sélection (calendarsTouched).
  const { clearDraft, mergeDraftOver } = useFormDraft<z.infer<typeof courseSchema>>(
    "course-form",
    form,
    { entityId: editingCourse?.id ?? null, restoreOnMount: false }
  );
  const extraDraftKey = () => {
    const { gymId, user } = useUserStore.getState();
    return buildDraftKey(
      `course-form-extra:${editingCourse?.id ?? "new"}`,
      gymId,
      user?.id ?? null
    );
  };

  // Sauvegarde de la sélection de plannings — uniquement après action réelle.
  useEffect(() => {
    if (!calendarsTouched) return;
    useFormDraftStore.getState().saveDraft(extraDraftKey(), { selectedCalendarIds });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCalendarIds, calendarsTouched]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Charger les plannings de la salle à l'ouverture, pour l'affectation du cours.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { getUserGymId } = await import('@/utils/gymAuth');
        const { listCalendars } = await import('@/services/edgeFunctionWrappers/calendarsWrapper');
        const gymId = getUserGymId();
        if (!gymId) return;
        const data = await listCalendars(gymId, false);
        if (!cancelled) setCalendars(data || []);
      } catch (e) {
        console.error('[AddCourseDialog] listCalendars failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (editingCourse) {
      form.reset(mergeDraftOver({
        name: editingCourse.name || "",
        description: editingCourse.description || "",
        category_id: editingCourse.category_id || "",
        default_capacity: editingCourse.default_capacity || 0,
        default_duration: editingCourse.default_duration || 60,
      }));
      // Préselection des plannings affectés (vide = cours universel).
      setSelectedCalendarIds(
        Array.isArray(editingCourse.calendar_ids) ? editingCourse.calendar_ids : []
      );
      setCalendarsTouched(false);
    } else {
      form.reset(mergeDraftOver({
        name: "",
        description: "",
        category_id: "",
        default_capacity: 10,
        default_duration: 60,
      }));
      setSelectedCalendarIds([]);
      setCalendarsTouched(false);
    }
    // Sélection de plannings brouillonnée : n'existe que si l'utilisateur y
    // avait réellement touché → sa restauration ré-arme calendarsTouched.
    const extra = useFormDraftStore.getState().getDraft(extraDraftKey());
    const savedIds = (extra?.data as { selectedCalendarIds?: string[] } | undefined)?.selectedCalendarIds;
    if (Array.isArray(savedIds)) {
      setSelectedCalendarIds(savedIds);
      setCalendarsTouched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingCourse, form, mergeDraftOver]);

  const toggleCalendar = (id: string) => {
    setCalendarsTouched(true);
    setSelectedCalendarIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (data: any) => {
    // En édition, on n'envoie `calendar_ids` que si l'on connaît l'état actuel
    // (cours chargé via la fonction edge) ou si l'utilisateur a modifié la
    // sélection. Sinon (`undefined`), le serveur ne touche pas aux affectations.
    const sendCalendarIds =
      !editingCourse ||
      Array.isArray(editingCourse.calendar_ids) ||
      calendarsTouched;

    const courseData = {
      ...data,
      ...(editingCourse ? { id: editingCourse.id } : {}),
      ...(sendCalendarIds ? { calendar_ids: selectedCalendarIds } : {}),
    };

    const success = await onSave(courseData);
    if (success) {
      clearDraft();
      useFormDraftStore.getState().clearDraft(extraDraftKey());
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {editingCourse ? t('editCourse', 'Edit Course') : t('addCourse')}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('courseName')}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t('courseName')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('description')}</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder={t('description')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('category')}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('selectCategory')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="default_capacity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('defaultCapacity')}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseInt(e.target.value) || 1)
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="default_duration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('defaultDuration')}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="15"
                        step="15"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseInt(e.target.value) || 15)
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {calendars.length > 0 && (
              <div className="space-y-2">
                <FormLabel>{t('assignedCalendars', 'Plannings')}</FormLabel>
                <div className="flex flex-wrap gap-2">
                  {calendars.map((cal) => {
                    const selected = selectedCalendarIds.includes(cal.id);
                    const color = resolveCalendarColor(cal.color, cal.id);
                    return (
                      <button
                        key={cal.id}
                        type="button"
                        onClick={() => toggleCalendar(cal.id)}
                        aria-pressed={selected}
                        className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-all"
                        style={
                          selected
                            ? { backgroundColor: color, borderColor: color, color: readableTextColor(color) }
                            : { borderColor: color, color }
                        }
                      >
                        {selected ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : (
                          <span
                            className="h-2.5 w-2.5 rounded-full"
                            style={{ backgroundColor: color }}
                          />
                        )}
                        {cal.name}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t(
                    'assignedCalendarsHint',
                    'Aucun planning sélectionné = cours visible sur tous les plannings.'
                  )}
                </p>
              </div>
            )}

            <DialogFooter>
              <Button type="submit">
                {editingCourse ? t('saveChanges', 'Save Changes') : t('addCourse')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
